import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { Card, Typography, CardContent, useTheme, CardHeader, Grid, Button, styled } from "@mui/material";

import Tooltip from "@mui/material/Tooltip";
import { IFlight, IFlightDisplayErrorPriorty, unassignedAircraftId, IFlightError } from "@vmosolutions/oc-interfaces";
import { FlightChip } from "src/components/Flights/FlightChip";
import { setFocusedEvents, setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import classNames from "classnames";
import { IFlightErrorMap } from "src/redux/reducers/FlightScannerReducer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface IFlightErrorInfo {
  flight: IFlight;
  error?: IFlightError;
}

interface IFlightErrorProps {
  flightError: IFlightErrorInfo;
}

const cardWidth = 500;
const rowHeight = 240;
const OCFlightError = styled(Card)(({ theme }) => ({
  maxWidth: cardWidth,
  maxHeight: rowHeight,
  width: "100%",
  paddingBottom: 0,
  "& .flightchip_container": {
    cursor: "pointer",
    position: "absolute",
    top: 12,
    right: 20,
  },
  "& .card_header": {
    height: "1rem",
    padding: 10,
  },
  "& .card_content": {
    maxHeight: rowHeight - 60,
    overflowY: "auto",
    padding: 10,
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& .flight_id": {
      position: "absolute",
      color: theme.palette.background.default,
      bottom: 0,
      right: 0,
      fontSize: 10,
      marginRight: 20,
      marginBottom: 10,
      fontWeight: 300,
    },
  },
  "&.compact": {
    margin: 2,
    maxWidth: cardWidth - 200,
    maxHeight: rowHeight - 90,
    "& .card_header": {
      height: "1rem",
      padding: 5,
      "& .MuiCardHeader-title": { fontSize: 12 },
    },
    "& .card_content": {
      maxHeight: rowHeight - 150,
      padding: 5,
      "& .MuiTypography-subtitle1": { fontSize: 12, fontWeight: 300 },
    },
  },
}));

export const FlightError = (props: IFlightErrorProps): JSX.Element => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const errorColor = theme.palette.error.dark;
  const warningColor = theme.custom.timeline.webglCanvas.clock; // Let's get a better defined color table for these.
  const backgroundColor = theme.custom.timeline.contextListBackground;
  const { flightError } = props;
  const flightErrorsMap: IFlightErrorMap = useSelector((state: IRootState) => state.flightScanner.flightErrorsMap);
  const [errorIndex, setErrorIndex] = useState(0);
  // Will be false when used in the context Menu.
  const displayInContext = !flightError.hasOwnProperty("error");

  // If there's no error showin in the data, search redux to see if there are any associated with the flight.
  const displayFlightErrors = useMemo(() => {
    if (!flightError?.flight) {
      return [flightError.error];
    }
    const flightId = flightError.flight._id;
    const aircraftId = flightError.flight.aircraft?._id ?? unassignedAircraftId;
    const flights = flightErrorsMap[aircraftId]?.errors.filter((value: IFlightError) => value.flightId === flightId);
    return flights;
  }, [flightErrorsMap, flightError]);

  // flightError stays mounted when context menu is open while the flight changes. This hook makes sure doing so
  // reverts the cards to the first error. Also does so if the current flight's # of errors change.
  useEffect(() => {
    setErrorIndex(0);
  }, [flightError?.flight?._id, displayFlightErrors?.length]);

  const displayFlightError = useMemo(() => {
    if (!displayFlightErrors) {
      return null;
    }
    return displayFlightErrors[errorIndex];
  }, [errorIndex, displayFlightErrors]);

  if (!displayFlightErrors || displayFlightErrors.length === 0) {
    return null;
  }

  const isCritical: boolean = displayFlightError?.priority === IFlightDisplayErrorPriorty.WARNING ? false : true;
  const cardHeaderColor = !isCritical ? warningColor : errorColor;

  const selectFlight = (flight: IFlight) => (flightId: string) => {
    dispatch(setSelectedEvents([flight]));
    dispatch(setFocusedEvents({ events: [flight], shouldFocus: "first" }));
  };

  return (
    <OCFlightError className={classNames(displayInContext && "compact")}>
      <Tooltip title={"Jump To Flight"} enterDelay={1000} placement="top">
        <div className="flightchip_container">
          {!displayInContext && flightError.flight ? (
            <FlightChip data={flightError.flight} onClickChip={selectFlight(flightError.flight)} />
          ) : null}
        </div>
      </Tooltip>

      <CardHeader
        className="card_header"
        title={displayInContext ? `Scan Status: ${displayFlightError?.priority}` : displayFlightError?.priority}
        style={{
          backgroundImage: `linear-gradient(to right, ${cardHeaderColor} 0%, ${backgroundColor} 100%)`,
        }}
      ></CardHeader>

      <CardContent className="card_content">
        <Typography variant="subtitle1">{displayFlightError?.title}</Typography>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <Button
              onClick={() => {
                setErrorIndex(errorIndex - 1 >= 0 ? errorIndex - 1 : displayFlightErrors.length - 1);
              }}
            >
              <ArrowBackIcon />
            </Button>
          </Grid>
          <Grid item>
            <span>
              {errorIndex + 1} of {displayFlightErrors.length}
            </span>
          </Grid>
          <Grid item>
            <Button onClick={() => setErrorIndex(errorIndex + 1 < displayFlightErrors.length ? errorIndex + 1 : 0)}>
              <ArrowForwardIcon />
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </OCFlightError>
  );
};
